import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { Award, BookOpen, CheckCircle2, ChevronRight, Sparkles } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function LandingScholarships() {
    return (
        <LandingLayout>
            <SeoHead
                title="Scholarships and Grants"
                description="Discover scholarship, grant, and financial assistance programs available at Northeastern College."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section id="scholarships" className="bg-muted pb-16">
                 
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/scholar.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                     
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />

                     
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                     
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Scholarships & Grants</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Scholarships &amp; Study Grants
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Explore academic scholarships, government grants, and financial aid programs available to help fund your education at NC.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="grid gap-6 lg:grid-cols-2">
                        <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                            
                            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-bold md:text-xl">
                                <Award className="size-5 text-[#0A7A35]" />
                                Financial Assistance Programs
                            </h3>
                            <p className="text-muted-foreground mb-4 text-sm leading-relaxed md:text-base">
                                The administration, in line with its mission statement, wishes to bring higher education within the reach of
                                financially needy but talented students. A number of Government Academic Scholarships, grants, discounts, and other
                                assistance programs are offered.
                            </p>
                            <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                Application for scholarship grants, student assistantship, and working students should be submitted to the President
                                through the Scholarship Coordinator in triplicate copies with the necessary supporting papers. Upon receipt, all
                                documents will be endorsed for approval.
                            </p>
                        </div>

                        <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                            
                            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-bold md:text-xl">
                                <Sparkles className="size-5 text-[#0A7A35]" />
                                Entrance Academic Scholarships
                            </h3>
                            <ul className="space-y-3 pl-1">
                                <li className="text-muted-foreground flex items-start gap-2.5 text-sm md:text-base">
                                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[#0A7A35]" />
                                    <span>
                                        <strong>Full Tuition Fee Scholarship</strong> - Granted to High School Valedictorians
                                    </span>
                                </li>
                                <li className="text-muted-foreground flex items-start gap-2.5 text-sm md:text-base">
                                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[#0A7A35]" />
                                    <span>
                                        <strong>Half-Tuition Fee Scholarship</strong> - Granted to High School Salutatorians
                                    </span>
                                </li>
                            </ul>
                            <p className="text-muted-foreground bg-muted/40 border-border/50 mt-6 rounded-xl border p-4 text-sm leading-relaxed">
                                These scholarships are valid for one semester and are renewable provided the student maintains the required academic
                                rating and has not violated any school rules and regulations.
                            </p>
                        </div>

                        <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                            
                            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-bold md:text-xl">
                                <Award className="size-5 text-[#0A7A35]" />
                                Academic Scholarships
                            </h3>
                            <ul className="mb-6 space-y-3 pl-1">
                                <li className="text-muted-foreground flex items-start gap-2.5 text-sm md:text-base">
                                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[#0A7A35]" />
                                    <span>
                                        <strong>91% - 94% average</strong> with no grade below 85% - FULL Tuition Fee Discount
                                    </span>
                                </li>
                                <li className="text-muted-foreground flex items-start gap-2.5 text-sm md:text-base">
                                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[#0A7A35]" />
                                    <span>
                                        <strong>88% - 90% average</strong> with no grade below 85% - HALF Tuition Fee Discount
                                    </span>
                                </li>
                            </ul>
                            <p className="text-muted-foreground mb-3 text-xs font-bold tracking-wider uppercase">Other criteria for evaluation:</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="text-muted-foreground bg-muted/40 border-border/50 rounded-xl border p-2.5 text-center text-xs font-semibold">
                                    Good Moral Character
                                </div>
                                <div className="text-muted-foreground bg-muted/40 border-border/50 rounded-xl border p-2.5 text-center text-xs font-semibold">
                                    No Pending Legal Cases
                                </div>
                                <div className="text-muted-foreground bg-muted/40 border-border/50 rounded-xl border p-2.5 text-center text-xs font-semibold lg:col-span-2">
                                    Demonstrated Leadership Ability
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                            
                            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-bold md:text-xl">
                                <BookOpen className="size-5 text-[#0A7A35]" />
                                Other Study Grants &amp; Scholarships
                            </h3>
                            <div className="text-muted-foreground grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 sm:text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    PESFA Financial Assistance
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    NISGP Study Grant
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    SEGEAP Ethnic Group Support
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Study Now Pay Later (SNPLP)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Rep. Antonio Abaya Grant
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Rotary Club of Santiago
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Dr. Inesita Jovonillo Grant
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Erap Foundation Scholarship
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Student Assistantship
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Athletic Scholarship
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    School Organ Editor-in-Chief
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Santiago City Scholarship
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8 lg:col-span-2">
                            <div className="absolute top-0 left-0 h-1 w-full bg-[#0A7A35]" />
                            <h3 className="text-foreground mb-4 flex items-center gap-2 pl-2 text-lg font-bold md:text-xl">
                                <CheckCircle2 className="size-5 text-[#0A7A35]" />
                                General Application Requirements
                            </h3>
                            <div className="text-muted-foreground grid grid-cols-1 gap-4 pl-2 text-sm leading-relaxed md:grid-cols-2">
                                <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="mt-0.5 size-4.5 shrink-0 text-[#0A7A35]" />
                                    <span>Income Tax Returns (PHP 45,000 and below for PESFA / PHP 60,000 and below for SNPLP)</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="mt-0.5 size-4.5 shrink-0 text-[#0A7A35]" />
                                    <span>Form 138 (Report Card) and NSAT Result</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="mt-0.5 size-4.5 shrink-0 text-[#0A7A35]" />
                                    <span>Certificate of Good Moral Character (GMRC)</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="mt-0.5 size-4.5 shrink-0 text-[#0A7A35]" />
                                    <span>Certificate of Rank from the Principal (for Government Scholars)</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="mt-0.5 size-4.5 shrink-0 text-[#0A7A35]" />
                                    <span>Must be in the Top 20 of the Graduating Class (PESFA / special grants)</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="mt-0.5 size-4.5 shrink-0 text-[#0A7A35]" />
                                    <span>Medical Certificate and GSIS/SSS Guarantor (for SNPLP loan applications)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
